import React, { useEffect, useRef } from 'react';
import { formatPercentage } from '../../utils/formatters';
import styles from './EntityNetworkGraph.module.css';

// Read-only visualization of the entity ownership network (owner --pct--> owned)
// on cytoscape + dagre, mirroring the existing TransactionNetworkVisualization
// pattern (dynamic import, container ref, destroy on unmount). Node fill colors
// are literal hex because they're painted on the cytoscape <canvas>, which can't
// resolve CSS custom properties — the documented exception for canvas rendering.
// Mirror the design-system semantic palette used by the kind Badge variants on
// the list (Person=info, Business=accent, Trust=warning, Estate=success) so a
// type reads as one color across the chips and the graph. The -600 shade gives
// white node labels enough contrast. Literal hex because cytoscape paints on a
// <canvas> and can't read CSS custom properties (the documented exception).
const KIND_COLORS = {
  Person: '#1d3faf',   // info-700
  Business: '#0e7490', // accent-700
  Trust: '#92400e',    // warning-700
  Estate: '#15603b',   // success-700
};
const DEFAULT_NODE_COLOR = '#57606a';

export default function EntityNetworkGraph({ entities = [], edges = [], selectedId, onSelectEntity }) {
  const containerRef = useRef(null);
  const cyRef = useRef(null);
  const resizeObsRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      if (!containerRef.current) return;
      const cytoscape = (await import('cytoscape')).default;

      let layout = { name: 'breadthfirst', directed: true, padding: 24, spacingFactor: 1.2 };
      try {
        const dagre = (await import('cytoscape-dagre')).default;
        cytoscape.use(dagre);
        // TB: the source of each :OWNS edge (the owner / beneficiary) ranks
        // ABOVE the entity it owns (business / trust / estate).
        layout = { name: 'dagre', rankDir: 'TB', padding: 24, nodeSep: 55, rankSep: 75, nodeDimensionsIncludeLabels: true };
      } catch (e) {
        // dagre optional — fall back to the built-in breadthfirst layout
      }
      if (cancelled || !containerRef.current) return;

      // Respect the per-entity view override: hidden entities (and any edge that
      // touches one) are dropped from the chart. View-only — the entity, its
      // ownership edges, and all forensic values are untouched in the data.
      const visibleEntities = entities.filter((e) => !e.hide_from_chart);
      const visibleIds = new Set(visibleEntities.map((e) => e.id));
      const elements = [
        ...visibleEntities.map((e) => ({
          data: { id: e.id, label: e.display_name || '(unnamed)', color: KIND_COLORS[e.kind] || DEFAULT_NODE_COLOR },
        })),
        ...edges
          .filter((ed) => ed.owner_id && ed.owned_id && visibleIds.has(ed.owner_id) && visibleIds.has(ed.owned_id))
          .map((ed) => ({
            data: {
              id: `${ed.owner_id}->${ed.owned_id}`,
              source: ed.owner_id,
              target: ed.owned_id,
              label: formatPercentage(ed.pct ?? 0),
            },
          })),
      ];

      const cy = cytoscape({
        container: containerRef.current,
        elements,
        style: [
          {
            selector: 'node',
            style: {
              'background-color': 'data(color)', label: 'data(label)', color: '#ffffff',
              'text-valign': 'center', 'text-halign': 'center', 'font-size': 11, 'font-weight': 'bold',
              // Node grows vertically to fit its (wrapped) label so longer business
              // names don't overflow the box; min-height keeps short labels uniform.
              width: 120, 'text-max-width': 104, height: 'label', 'min-height': 38,
              padding: '6px', shape: 'round-rectangle', 'text-wrap': 'wrap',
              'border-width': 2, 'border-color': '#d0d7de',
            },
          },
          { selector: 'node:selected', style: { 'border-width': 4, 'border-color': '#0b3d2e' } },
          {
            selector: 'edge',
            style: {
              width: 2, 'line-color': '#8c959f', 'target-arrow-color': '#8c959f',
              'target-arrow-shape': 'triangle', 'curve-style': 'bezier', label: 'data(label)',
              'font-size': 10, color: '#57606a', 'text-background-color': '#ffffff',
              'text-background-opacity': 0.9, 'text-background-padding': 2,
            },
          },
        ],
        layout,
        wheelSensitivity: 0.3,
        minZoom: 0.2,
        maxZoom: 3,
      });

      cy.on('tap', 'node', (evt) => {
        if (onSelectEntity) onSelectEntity(evt.target.id());
      });
      cyRef.current = cy;

      // Keep the graph filling its (responsive) container: when the box resizes
      // — window resize, panel toggle, the height clamp changing — tell
      // cytoscape to re-measure and re-fit so the layout uses the new space
      // instead of staying pinned at its mount-time size.
      if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(() => {
          const c = cyRef.current;
          if (c) { c.resize(); c.fit(undefined, 30); }
        });
        ro.observe(containerRef.current);
        resizeObsRef.current = ro;
      }
    }

    init();
    return () => {
      cancelled = true;
      if (resizeObsRef.current) {
        resizeObsRef.current.disconnect();
        resizeObsRef.current = null;
      }
      if (cyRef.current) {
        cyRef.current.destroy();
        cyRef.current = null;
      }
    };
    // Rebuild when the network changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entities, edges]);

  // Reflect the externally-selected entity onto the graph.
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.nodes().unselect();
    if (selectedId) {
      const node = cy.getElementById(selectedId);
      if (node && node.nonempty()) node.select();
    }
  }, [selectedId]);

  if (!entities.length) {
    return <div className={styles.empty}>No entities yet — create one to start the ownership network.</div>;
  }
  if (!entities.some((e) => !e.hide_from_chart)) {
    return <div className={styles.empty}>All entities are hidden from the chart. Use the eye toggle to show them.</div>;
  }
  return <div ref={containerRef} className={styles.graph} aria-label="Entity ownership network" />;
}
